import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Form,
	FormGroup,
	Label,
	Input,
	FormText,
	Row,
	Col,
	Spinner,
	CustomInput,
} from 'reactstrap';
import '../Index.css';

const initialForm = {
	title: '',
	description: '',
	images: '',
	building: '',
	serviceDate: '',
	bookingDeadline: '',
	originalPrice: '',
	discountedPrice: '',
	maxBookings: '',
	minBookingsRequired: '',
	cancellationPolicy: '',
	terms: '',
	publishNow: false,
};

const toDateTimeLocal = (value) => {
	if (!value) return '';

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';

	const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
	return offsetDate.toISOString().slice(0, 16);
};

const getInitialForm = (deal) => {
	if (!deal) return initialForm;

	const targetBuilding = deal.targetBuildings?.[0]?.building;
	const buildingId =
		typeof targetBuilding === 'string' ? targetBuilding : targetBuilding?._id;

	return {
		title: deal.title || '',
		description: deal.description || '',
		images: Array.isArray(deal.images) ? deal.images.join(', ') : '',
		building: buildingId || '',
		serviceDate: toDateTimeLocal(deal.serviceDate),
		bookingDeadline: toDateTimeLocal(deal.bookingDeadline),
		originalPrice:
			deal.originalPrice || deal.originalPrice === 0 ? String(deal.originalPrice) : '',
		discountedPrice:
			deal.discountedPrice || deal.discountedPrice === 0
				? String(deal.discountedPrice)
				: '',
		maxBookings: deal.maxBookings ? String(deal.maxBookings) : '',
		minBookingsRequired: deal.minBookingsRequired
			? String(deal.minBookingsRequired)
			: '',
		cancellationPolicy: deal.cancellationPolicy || '',
		terms: deal.terms || '',
		publishNow: false,
	};
};

const buildDiscountLabel = (originalPrice, discountedPrice) => {
	if (originalPrice === '' || discountedPrice === '') return '';

	return `${Math.round(
		((Number(originalPrice) - Number(discountedPrice)) /
			Number(originalPrice)) *
			100,
	)}% OFF`;
};

const DealFormModal = ({
	isOpen,
	toggle,
	mode = 'create',
	initialDeal = null,
	onSubmit,
	onPublishAfterCreate,
}) => {
	const myBusiness = useSelector((state) => state.myBusiness);
	const groupDealsState = useSelector((state) => state.groupDeals);

	const businessBuildings = useMemo(() => {
		return Array.isArray(myBusiness?.buildings) ? myBusiness.buildings : [];
	}, [myBusiness]);

	const isEditMode = mode === 'edit';
	const isPublished = initialDeal?.status === 'published';
	const canEditCoreFields = !isEditMode || initialDeal?.status === 'draft';

	const [formData, setFormData] = useState(initialForm);
	const [submitting, setSubmitting] = useState(false);
	const [localError, setLocalError] = useState('');

	useEffect(() => {
		if (isOpen) {
			setFormData(getInitialForm(initialDeal));
			setLocalError('');
		}
	}, [initialDeal, isOpen]);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const resetForm = () => {
		setFormData(getInitialForm(initialDeal));
		setLocalError('');
	};

	const handleClose = () => {
		if (submitting) return;
		resetForm();
		toggle();
	};

	const validateForm = () => {
		if (!formData.title.trim()) return 'Title is required';
		if (!formData.description.trim()) return 'Description is required';

		if (!canEditCoreFields) {
			return '';
		}

		if (!formData.building) return 'Please select a building';
		if (!formData.serviceDate) return 'Service date is required';
		if (!formData.bookingDeadline) return 'Booking deadline is required';
		if (!formData.originalPrice) return 'Original price is required';
		if (!formData.discountedPrice) return 'Discounted price is required';
		if (!formData.maxBookings) return 'Max bookings is required';
		if (!formData.minBookingsRequired)
			return 'Minimum required bookings is required';

		const originalPrice = Number(formData.originalPrice);
		const discountedPrice = Number(formData.discountedPrice);
		const maxBookings = Number(formData.maxBookings);
		const minBookingsRequired = Number(formData.minBookingsRequired);
		const serviceDate = new Date(formData.serviceDate);
		const bookingDeadline = new Date(formData.bookingDeadline);
		const now = new Date();

		if (Number.isNaN(originalPrice) || originalPrice <= 0) {
			return 'Original price must be greater than 0';
		}

		if (Number.isNaN(discountedPrice) || discountedPrice < 0) {
			return 'Discounted price must be 0 or greater';
		}

		if (discountedPrice >= originalPrice) {
			return 'Discounted price must be less than original price';
		}

		if (Number.isNaN(maxBookings) || maxBookings < 1) {
			return 'Max bookings must be at least 1';
		}

		if (Number.isNaN(minBookingsRequired) || minBookingsRequired < 1) {
			return 'Minimum required bookings must be at least 1';
		}

		if (minBookingsRequired > maxBookings) {
			return 'Minimum required bookings cannot exceed max bookings';
		}

		if (
			isEditMode &&
			initialDeal?.bookedCount &&
			maxBookings < initialDeal.bookedCount
		) {
			return 'Max bookings cannot be below current booked count';
		}

		if (serviceDate <= now) {
			return 'Service date must be in the future';
		}

		if (bookingDeadline <= now) {
			return 'Booking deadline must be in the future';
		}

		if (bookingDeadline >= serviceDate) {
			return 'Booking deadline must be before service date';
		}

		return '';
	};

	const buildPayload = () => {
		const contentPayload = {
			title: formData.title.trim(),
			description: formData.description.trim(),
			images: formData.images
				.split(',')
				.map((img) => img.trim())
				.filter(Boolean),
			cancellationPolicy: formData.cancellationPolicy.trim(),
			terms: formData.terms.trim(),
		};

		if (!canEditCoreFields) {
			return contentPayload;
		}

		return {
			...contentPayload,
			targetBuildings: [
				{
					building: formData.building,
				},
			],
			serviceDate: formData.serviceDate,
			bookingDeadline: formData.bookingDeadline,
			originalPrice: Number(formData.originalPrice),
			discountedPrice: Number(formData.discountedPrice),
			maxBookings: Number(formData.maxBookings),
			minBookingsRequired: Number(formData.minBookingsRequired),
			currency: 'CAD',
			discountLabel: buildDiscountLabel(
				formData.originalPrice,
				formData.discountedPrice,
			),
			paymentMode: 'offline',
		};
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLocalError('');

		const validationError = validateForm();
		if (validationError) {
			setLocalError(validationError);
			return;
		}

		setSubmitting(true);

		try {
			const savedDeal = await onSubmit(buildPayload());

			if (
				!isEditMode &&
				formData.publishNow &&
				savedDeal?._id &&
				onPublishAfterCreate
			) {
				await onPublishAfterCreate(savedDeal._id);
			}

			handleClose();
		} catch (err) {
			setLocalError(
				err?.response?.data?.message || err?.message || 'Failed to save deal',
			);
		} finally {
			setSubmitting(false);
		}
	};

	const modalTitle = isEditMode ? 'Edit Group Deal' : 'Create Group Deal';
	const introText = isEditMode
		? isPublished
			? 'Update the public-facing content for this published deal.'
			: 'Update the offer details before residents book it.'
		: 'Create a building-specific offer with limited slots, clear timing, and strong resident urgency.';

	const submitLabel = isEditMode
		? 'Save Changes'
		: formData.publishNow
			? 'Create & Publish'
			: 'Save Deal';

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size='lg'
			centered
			className='create-deal-modal'>
			<ModalHeader toggle={handleClose}>{modalTitle}</ModalHeader>

			<Form onSubmit={handleSubmit}>
				<ModalBody className='create-deal-modal__body'>
					<div className='create-deal-modal__intro'>{introText}</div>

					{localError ? (
						<div className='create-deal-modal__error'>{localError}</div>
					) : null}

					<Row form>
						<Col md={12}>
							<FormGroup>
								<Label for='title'>Deal Title</Label>
								<Input
									id='title'
									name='title'
									type='text'
									placeholder='50% OFF Deep Cleaning'
									value={formData.title}
									onChange={handleChange}
								/>
							</FormGroup>
						</Col>

						<Col md={12}>
							<FormGroup>
								<Label for='description'>Description</Label>
								<Input
									id='description'
									name='description'
									type='textarea'
									rows='4'
									placeholder='Describe the offer, what is included, and why this is special for residents.'
									value={formData.description}
									onChange={handleChange}
								/>
							</FormGroup>
						</Col>

						<Col md={12}>
							<FormGroup>
								<Label for='images'>Image URLs</Label>
								<Input
									id='images'
									name='images'
									type='textarea'
									rows='3'
									placeholder='Paste one or more image URLs separated by commas'
									value={formData.images}
									onChange={handleChange}
								/>
								<FormText color='muted'>
									For now, enter image URLs separated by commas. We can add file
									upload next.
								</FormText>
							</FormGroup>
						</Col>

						<Col md={12}>
							<FormGroup>
								<Label for='building'>Target Building</Label>
								<Input
									id='building'
									name='building'
									type='select'
									value={formData.building}
									onChange={handleChange}
									disabled={!canEditCoreFields}>
									<option value=''>Select a building</option>
									{businessBuildings.map((building) => (
										<option key={building._id} value={building._id}>
											{building.address}
										</option>
									))}
								</Input>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='serviceDate'>Service Date</Label>
								<Input
									id='serviceDate'
									name='serviceDate'
									type='datetime-local'
									value={formData.serviceDate}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='bookingDeadline'>Booking Deadline</Label>
								<Input
									id='bookingDeadline'
									name='bookingDeadline'
									type='datetime-local'
									value={formData.bookingDeadline}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='originalPrice'>Original Price</Label>
								<Input
									id='originalPrice'
									name='originalPrice'
									type='number'
									min='0'
									step='0.01'
									placeholder='100'
									value={formData.originalPrice}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='discountedPrice'>Discounted Price</Label>
								<Input
									id='discountedPrice'
									name='discountedPrice'
									type='number'
									min='0'
									step='0.01'
									placeholder='50'
									value={formData.discountedPrice}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='maxBookings'>Max Bookings</Label>
								<Input
									id='maxBookings'
									name='maxBookings'
									type='number'
									min='1'
									placeholder='10'
									value={formData.maxBookings}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup>
								<Label for='minBookingsRequired'>Min Required</Label>
								<Input
									id='minBookingsRequired'
									name='minBookingsRequired'
									type='number'
									min='1'
									placeholder='10'
									value={formData.minBookingsRequired}
									onChange={handleChange}
									disabled={!canEditCoreFields}
								/>
							</FormGroup>
						</Col>

						<Col md={12}>
							<FormGroup>
								<Label for='cancellationPolicy'>Cancellation Policy</Label>
								<Input
									id='cancellationPolicy'
									name='cancellationPolicy'
									type='textarea'
									rows='3'
									placeholder='Explain cancellation, refund, or failure rules.'
									value={formData.cancellationPolicy}
									onChange={handleChange}
								/>
							</FormGroup>
						</Col>

						<Col md={12}>
							<FormGroup>
								<Label for='terms'>Terms</Label>
								<Input
									id='terms'
									name='terms'
									type='textarea'
									rows='3'
									placeholder='Add important conditions, requirements, or service notes.'
									value={formData.terms}
									onChange={handleChange}
								/>
							</FormGroup>
						</Col>

						<Col md={12}>
							<div className='create-deal-modal__summary'>
								<div className='create-deal-modal__summary-title'>
									Quick Preview
								</div>
								<div className='create-deal-modal__summary-grid'>
									<div>
										<span>Discount</span>
										<strong>
											{formData.originalPrice && formData.discountedPrice
												? buildDiscountLabel(
														formData.originalPrice,
														formData.discountedPrice,
													)
												: '--'}
										</strong>
									</div>
									<div>
										<span>Slots</span>
										<strong>
											{formData.maxBookings
												? `${formData.minBookingsRequired || 0}/${formData.maxBookings}`
												: '--'}
										</strong>
									</div>
									<div>
										<span>Status</span>
										<strong>
											{isEditMode
												? initialDeal?.status || 'draft'
												: formData.publishNow
													? 'Publish now'
													: 'Save as draft'}
										</strong>
									</div>
								</div>
							</div>
						</Col>

						{!isEditMode ? (
							<Col md={12}>
								<FormGroup className='create-deal-modal__toggle'>
									<CustomInput
										id='publishNow'
										name='publishNow'
										type='switch'
										label='Publish immediately after creation'
										checked={formData.publishNow}
										onChange={handleChange}
									/>
								</FormGroup>
							</Col>
						) : null}
					</Row>
				</ModalBody>

				<ModalFooter className='create-deal-modal__footer'>
					<Button
						type='button'
						color='secondary'
						outline
						onClick={handleClose}
						disabled={submitting}>
						Cancel
					</Button>

					<Button
						type='submit'
						className='create-deal-modal__submit'
						disabled={submitting || groupDealsState.loading}>
						{submitting ? (
							<>
								<Spinner size='sm' className='mr-2' />
								Saving...
							</>
						) : (
							submitLabel
						)}
					</Button>
				</ModalFooter>
			</Form>
		</Modal>
	);
};

export default DealFormModal;
